import React, { useState } from 'react';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button'
import { ArrowUpRight } from 'react-feather';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { Spinner } from 'react-bootstrap';

import Section from '../shared/Section';
import { EventsDataUnit, SignUpFormState, OrganisaterDataUnit, EventSlotDataUnit } from './types/EventsData';
import { Linkedin } from 'react-feather';
import { sanitizeHtml } from '../../utils/sanitizeHtml';
import { fetchFormToken } from '../../utils/formToken';
import { resolveLang } from '../../utils/resolveLang';

import './event.css';

const Event = (): React.JSX.Element => {

    const { id, lang: urlLang } = useParams<{ id: string; lang: string }>();
    const { t } = useTranslation();
    const [loading, setLoading] = React.useState(false);

    const [event, setEvent] = React.useState<EventsDataUnit | null | undefined>(undefined);
    const [organisaters, setOrganisater] = React.useState<OrganisaterDataUnit[] | null | undefined>(undefined);
    const [eventSlots, setEventSlots] = React.useState<EventSlotDataUnit[] | null | undefined>(undefined);

    const [validated, setValidated] = useState(false);
    const [result, setResult] = useState(200);

    const [vodEmail, setVodEmail] = useState("");
    const [vodFirstName, setVodFirstName] = useState("");
    const [vodLastName, setVodLastName] = useState("");
    const [vodEnterpriseName, setVodEnterpriseName] = useState("");
    // Champ piege (honeypot, issue #10) du formulaire Replay.
    const [vodPhone, setVodPhone] = useState("");
    const [vodValidated, setVodValidated] = useState(false);
    const [vodResult, setVodResult] = useState(200);
    const [loadingVod, setLoadingVod] = useState(false);
    // Issue #10 (VIT-06) : jeton de delai, recupere a l'affichage et partage par les deux
    // formulaires de la page (inscription et Replay).
    const [formToken, setFormToken] = useState("");

    React.useEffect(() => {
        const lang = resolveLang(urlLang);

        setEvent(undefined);
        setOrganisater(undefined);
        setEventSlots(undefined);

        const ac = new AbortController();

        const fetchEvent = async () => {
            try {
                const res = await fetch(`/event/${id}?lang=${lang}`, { signal: ac.signal });
                if (res.status === 404) { setEvent(null); return; }
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                const json = await res.json();
                setEvent(json?.data ?? null);
            } catch (e) {
                if ((e as any)?.name !== 'AbortError') {
                    console.error("Erreur de chargement de l'événement :", e);
                    setEvent(null);
                }
            }
        };
        const fetchOrganisater = async () => {
            try {
                const res = await fetch(`/organisater/${id}?lang=${lang}`, { signal: ac.signal });
                if (!res.ok) { setOrganisater(null); return; }
                const json = await res.json();
                setOrganisater(json?.data ?? null);
            } catch (e) {
                if ((e as any)?.name !== 'AbortError') {
                    console.error("Erreur de chargement des organisateurs :", e);
                    setOrganisater(null);
                }
            }
        };
        const fetchEventSlots = async () => {
            try {
                const res = await fetch(`/EventSlot/${id}?lang=${lang}`, { signal: ac.signal });
                if (!res.ok) { setEventSlots(null); return; }
                const json = await res.json();
                setEventSlots(json?.data ?? null);
            } catch (e) {
                if ((e as any)?.name !== 'AbortError') {
                    console.error("Erreur de chargement des créneaux :", e);
                    setEventSlots(null);
                }
            }
        };
        fetchEvent();
        fetchOrganisater();
        fetchEventSlots();
        fetchFormToken(ac.signal).then(setFormToken);
        return () => ac.abort();
    }, [id, urlLang]);

    const formatTime = (date: Date) =>
        date.toLocaleTimeString('fr-FR', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: false,
        });

    let eventStartDate: Date | null = null;
    let eventEndDate: Date | null = null;
    let event_passed: boolean | null = null;
    let formattedHour: string | undefined = undefined;
    if (event && typeof event === 'object') {
        eventStartDate = new Date(event.eventStart);
        eventEndDate = new Date(event.eventEnd);

        const sameHour =
            eventStartDate.getHours() === eventEndDate.getHours() &&
            eventStartDate.getMinutes() === eventEndDate.getMinutes();
        // meme Heure
        formattedHour = sameHour ? `${formatTime(eventStartDate)}` : `${formatTime(eventStartDate)} - ${formatTime(eventEndDate)}`;

        const now = new Date();
        event_passed = eventStartDate instanceof Date && !isNaN(eventStartDate.getTime())
            ? eventStartDate.getTime() < now.getTime() : false;
    } else {
        formattedHour = "Heure non Disponible"
    }


    const [formData, setFormData] = useState<SignUpFormState>({
        lastname: "",
        firstname: "",
        enterprisename: "",
        title: "",
        email: "",
        eventId: Number(id),
        phone: ""
    });

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData(prevData => ({ ...prevData, [name]: value }))
    };

    const handleSlotChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const { value } = e.target;
        setFormData(prevData => ({ ...prevData, slotId: value ? Number(value) : undefined }))
    };

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        const lang = resolveLang(urlLang);
        const form = event.currentTarget;
        event.preventDefault();
        setValidated(true);
        if (form.checkValidity() === false) {
            event.stopPropagation();
        } else {
            setLoading(true);
            try {
                const response = await fetch(`/EventForm?lang=${lang}`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({ ...formData, formToken }),
                });

                const resultJson = await response.json();
                setResult(resultJson.code);
                if (resultJson.code >= 200 && resultJson.code < 300) {
                    setValidated(true);
                    setLoading(false);
                    setTimeout(() => {
                    }, 3000)
                    handleReset();
                    // Le jeton est a usage unique : il vient d'etre consomme. Sans en
                    // reprendre un, une seconde inscription depuis la meme page serait
                    // refusee par une facade -- succes affiche, rien enregistre.
                    fetchFormToken().then(setFormToken);
                } else {
                    setValidated(false);
                    setLoading(false);
                    setTimeout(() => {
                    }, 3000)
                }
            } catch (error) {
                console.error('Erreur POST /EventForm :', error);
                setLoading(false);
            }
        }
    };

    const handleReset = () => {
        setFormData({
            lastname: "",
            firstname: "",
            enterprisename: "",
            title: "",
            email: "",
            eventId: Number(id),
            phone: ""
        })
    }

    const handleVodChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        if (name === "phone") setVodPhone(value);
        if (name === "email") setVodEmail(value);
        else if (name === "firstname") setVodFirstName(value);
        else if (name === "lastname") setVodLastName(value);
        else if (name === "enterprisename") setVodEnterpriseName(value);
    };

    const handleVodSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        const lang = resolveLang(urlLang);
        const form = event.currentTarget;
        event.preventDefault();

        if (form.checkValidity() === false) {
            event.stopPropagation();
            return;
        }

        setLoadingVod(true);
        try {
            const response = await fetch(`/VodRequest?lang=${lang}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    email: vodEmail,
                    firstname: vodFirstName,
                    lastname: vodLastName,
                    enterprisename: vodEnterpriseName,
                    eventId: Number(id),
                    phone: vodPhone,
                    formToken
                }),

            });

            const resultJson = await response.json();
            setVodResult(resultJson.code);

            if (resultJson.code >= 200 && resultJson.code < 300) {
                // Jeton consomme : on en reprend un pour qu'une seconde demande depuis la
                // meme page ne soit pas refusee en silence.
                fetchFormToken().then(setFormToken);
                setVodValidated(true);
                setTimeout(() => {
                    setVodEmail("");
                    setVodFirstName("");
                    setVodLastName("");
                    setVodEnterpriseName("");
                    setVodValidated(false);
                    setVodResult(200);
                }, 5000);
            } else {
                setVodValidated(false);
            }
        } catch (error) {
            console.error('Erreur POST /VodRequest :', error);
            setVodResult(500);
            setVodValidated(false);
        } finally {
            setLoadingVod(false);
        }
    };

    if (event === undefined) {
        return (
            <h6 className="detail text-center" dangerouslySetInnerHTML={{ __html: t("Pages.Events.Wait")! }} />
        );
    }

    if (event === null) {
        return (
            <h6 className="detail text-center" dangerouslySetInnerHTML={{ __html: t("Pages.Events.NotFound")! }} />
        );
    }
    function getInitials(name: string) {
        if (!name) return "";
        const parts = name.split(" ");
        return parts.slice(0, 2).map((n) => n[0].toUpperCase()).join("");
    }

    return (
        <>
            <Section className="event-header-section">
                <Row className="event-header-section py-4">
                    <Col lg={1}>
                        <div className="sevent-sidebar">
                            <div className="sevent-content">
                                <a
                                    href="https://www.linkedin.com/company/flowly1/"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="sevent-sidebar"
                                >
                                    <Linkedin size="18" />
                                </a>
                                <a
                                    href="https://www.linkedin.com/company/flowly1/"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="sevent-divider forceLeft"
                                />
                                <a
                                    href="https://www.linkedin.com/company/flowly1/"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="sevent-text"
                                >
                                    {t("NavBar.FollowUs")}
                                </a>
                            </div>
                        </div>
                    </Col>
                    <Col lg={10} xs={12} style={{ zIndex: 98 }}>
                        <h3 className="title">{event.title}</h3>
                        <p className="detail">
                            {event.eventPeriod}, {formattedHour}{event.eventTimeNote?.trim() ? ` ${event.eventTimeNote.trim()}` : ''}
                        </p>
                        {event.location === "webinar" ? (
                            <p
                                className="detail"
                                dangerouslySetInnerHTML={{ __html: t("Pages.Events.Webinar")! }}
                            />
                        ) : (
                            <p className="detail">{event.location}</p>
                        )}
                    </Col>
                    <Col xs={0} sm={0} md={1} lg={1} />
                </Row>
            </Section>
            <Section className="event-section">
                <Row className="container">
                    <Row className="information-container">
                        <Col xs={12} md={7} lg={7}>
                            <div className="description" dangerouslySetInnerHTML={{ __html: sanitizeHtml(event.description) }}></div>
                            {organisaters &&
                                <div className="organisater-list">
                                    {organisaters.map((org) => (
                                        <div key={org.id} className="organisater-item-bubble">
                                            <div className="organisater-image-container">
                                                {org.imgBase64 ? (
                                                    <img
                                                        src={org.imgBase64}
                                                        alt={org.name ?? "Organizer Image"}
                                                        className="organisater-image"
                                                    />
                                                ) : (
                                                    <div className="organisater-placeholder-image">
                                                        {getInitials(org.name)}
                                                    </div>
                                                )}
                                            </div>
                                            <div className="organisater-info">
                                                <div className="organisater-name">{org.name}</div>
                                                <div className="organisater-enterprise">{org.enterpriseName} - {org.role}</div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            }
                        </Col>
                        <Col xs={12} md={5} lg={5}>
                            <img className="img" src={event.eventImgBase64} alt={event.title} />
                        </Col>
                    </Row>
                    {!event_passed && (
                        <div className="form-container">
                            <Form className="form-content" onSubmit={handleSubmit}>
                                <Row className="form-elements">
                                    {event.location === "webinar" ? (
                                        <h5
                                            className="form-title"
                                            dangerouslySetInnerHTML={{
                                                __html: t("Pages.Events.Form.HeaderWebinar"),
                                            }}
                                        />
                                    ) : (
                                        <h5
                                            className="form-title"
                                            dangerouslySetInnerHTML={{
                                                __html: t("Pages.Events.Form.HeaderStand"),
                                            }}
                                        />
                                    )}
                                </Row>

                                <Row className="form-elements">
                                    <Form.Group
                                        className="form-group"
                                        as={Col}
                                        xs={12}
                                        md={6}
                                        controlId="validationCustom01"
                                    >
                                        <Form.Label className="text-left fw-bold w-100">
                                            {t("Pages.Events.Form.LastName")}
                                        </Form.Label>
                                        <Form.Control
                                            className="form-input"
                                            onChange={handleChange}
                                            value={formData.lastname}
                                            name="lastname"
                                            maxLength={100}
                                            required
                                            type="text"
                                            autoComplete="off"
                                        />
                                    </Form.Group>

                                    <Form.Group
                                        className="form-group"
                                        as={Col}
                                        xs={12}
                                        md={6}
                                        controlId="validationCustom02"
                                    >
                                        <Form.Label className="text-left fw-bold w-100">
                                            {t("Pages.Events.Form.FirstName")}
                                        </Form.Label>
                                        <Form.Control
                                            className="form-input"
                                            onChange={handleChange}
                                            value={formData.firstname}
                                            name="firstname"
                                            maxLength={100}
                                            required
                                            type="text"
                                            autoComplete="off"
                                        />
                                    </Form.Group>
                                </Row>

                                <Row className="form-elements">
                                    <Form.Group
                                        className="form-group"
                                        as={Col}
                                        xs={12}
                                        md={6}
                                        controlId="validationCustom03"
                                    >
                                        <Form.Label className="text-left fw-bold w-100">
                                            {t("Pages.Events.Form.EnterpriseName")}
                                        </Form.Label>
                                        <Form.Control
                                            className="form-input"
                                            onChange={handleChange}
                                            value={formData.enterprisename}
                                            name="enterprisename"
                                            maxLength={150}
                                            required
                                            type="text"
                                            autoComplete="off"
                                        />
                                    </Form.Group>

                                    <Form.Group
                                        className="form-group"
                                        as={Col}
                                        xs={12}
                                        md={6}
                                        controlId="validationCustom04"
                                    >
                                        <Form.Label className="text-left fw-bold w-100">
                                            {t("Pages.Events.Form.Title")}
                                        </Form.Label>
                                        <Form.Control
                                            className="form-input"
                                            onChange={handleChange}
                                            value={formData.title}
                                            name="title"
                                            maxLength={150}
                                            required
                                            type="text"
                                            autoComplete="off"
                                        />
                                    </Form.Group>
                                </Row>

                                <Row className="form-elements">
                                    <Form.Group
                                        className="form-group"
                                        as={Col}
                                        xs={12}
                                        md={6}
                                        controlId="validationCustom05"
                                    >
                                        <Form.Label className="text-left fw-bold w-100">
                                            {t("Pages.Events.Form.Email")}
                                        </Form.Label>
                                        <Form.Control
                                            className="form-input"
                                            onChange={handleChange}
                                            value={formData.email}
                                            name="email"
                                            maxLength={254}
                                            required
                                            type="email"
                                            autoComplete="off"
                                        />
                                    </Form.Group>

                                    {/* Champ piège (honeypot, issue #10) : invisible pour un
                                        humain, rempli par les robots qui remplissent tout ce
                                        qu'ils trouvent. Le serveur refuse alors la soumission
                                        en répondant comme un succès. Sans `required` ni
                                        validation, pour ne rien signaler au robot. */}
                                    <Form.Control
                                        onChange={handleChange}
                                        value={formData.phone}
                                        name="phone"
                                        type="hidden"
                                        autoComplete="off"
                                        tabIndex={-1}
                                    />

                                    {eventSlots && eventSlots.length > 0 && (
                                        <Form.Group
                                            className="form-group"
                                            as={Col}
                                            xs={12}
                                            md={6}
                                            controlId="validationCustomSlot"
                                        >
                                            <Form.Label className="text-left fw-bold w-100">
                                                {t("Pages.Events.Form.Slot")}
                                            </Form.Label>
                                            <Form.Select
                                                className="form-input"
                                                onChange={handleSlotChange}
                                                value={formData.slotId ?? ""}
                                                name="slotId"
                                                required
                                            >
                                                <option value="" disabled>
                                                    {t("Pages.Events.Form.SlotPlaceholder")}
                                                </option>
                                                {eventSlots.map((slot) => (
                                                    <option key={slot.id} value={slot.id}>
                                                        {slot.label}
                                                    </option>
                                                ))}
                                            </Form.Select>
                                        </Form.Group>
                                    )}
                                </Row>

                                <Row className="form-btn-elements">
                                    {!loading && (
                                        <>
                                            <Col xs="auto">
                                                <Button
                                                    className="flowly-btn orange"
                                                    type="submit"
                                                    dangerouslySetInnerHTML={{ __html: t("Pages.Events.Form.Send") }}
                                                />
                                            </Col>
                                            <Col xs="auto" className="pt-2">
                                                {result === 201 && validated && (
                                                    <Form.Label
                                                        dangerouslySetInnerHTML={{ __html: t("Pages.Events.Form.Success") }}
                                                    />
                                                )}
                                                {result >= 300 && !validated && (
                                                    <Form.Label
                                                        dangerouslySetInnerHTML={{ __html: t("Pages.Events.Form.Fail") }}
                                                    />
                                                )}
                                            </Col>
                                        </>
                                    )}
                                </Row>

                                {loading && <Spinner animation="border" />}
                            </Form>
                        </div>
                    )}
                    {/*{event_passed && (*/}
                    {/*    <Row>*/}
                    {/*        <h6 dangerouslySetInnerHTML={{ __html: t("Pages.Events.Broadcast") }} />*/}
                    {/*        <a href={event.urlBroadcast} target="_blank" rel="noopener noreferrer">*/}
                    {/*            {event.title}*/}
                    {/*        </a>*/}
                    {/*    </Row>*/}
                    {/*)}*/}
                    {event_passed && (
                        <div className="form-container">
                            <Form className="form-content" onSubmit={handleVodSubmit}>
                                <Row>
                                    <h5>{t("Pages.Events.VODHeader")}</h5>
                                </Row>

                                <Row className="form-elements">
                                    <Form.Group className="form-group" as={Col} xs={12} md={6} controlId="vodFirstName">
                                        <Form.Label>{t("Pages.Events.Form.FirstName")}</Form.Label>
                                        <Form.Control
                                            className="form-input"
                                            type="text"
                                            name="firstname"
                                            maxLength={100}
                                            value={vodFirstName}
                                            onChange={handleVodChange}
                                            required
                                        />
                                    </Form.Group>

                                    <Form.Group className="form-group" as={Col} xs={12} md={6} controlId="vodLastName">
                                        <Form.Label>{t("Pages.Events.Form.LastName")}</Form.Label>
                                        <Form.Control
                                            className="form-input"
                                            type="text"
                                            name="lastname"
                                            maxLength={100}
                                            value={vodLastName}
                                            onChange={handleVodChange}
                                            required
                                        />
                                    </Form.Group>
                                </Row>

                                <Row className="form-elements">
                                    <Form.Group className="form-group" as={Col} xs={12} md={6} controlId="vodEnterpriseName">
                                        <Form.Label>{t("Pages.Events.Form.EnterpriseName")}</Form.Label>
                                        <Form.Control
                                            className="form-input"
                                            type="text"
                                            name="enterprisename"
                                            maxLength={150}
                                            value={vodEnterpriseName}
                                            onChange={handleVodChange}
                                            required
                                        />
                                    </Form.Group>

                                    <Form.Group className="form-group" as={Col} xs={12} md={6} controlId="vodEmail">
                                        <Form.Label>{t("Pages.Events.Form.Email")}</Form.Label>
                                        <Form.Control
                                            className="form-input"
                                            type="email"
                                            name="email"
                                            maxLength={254}
                                            value={vodEmail}
                                            onChange={handleVodChange}
                                            required
                                        />
                                    </Form.Group>

                                    {/* Champ piège (honeypot, issue #10) : invisible pour un
                                        humain. Chaque requête acceptée ici envoie un mail vers
                                        deux boîtes nominatives et consomme le quota Graph du
                                        tenant -- c'est le formulaire où le piège compte le plus. */}
                                    <Form.Control
                                        onChange={handleVodChange}
                                        value={vodPhone}
                                        name="phone"
                                        type="hidden"
                                        autoComplete="off"
                                        tabIndex={-1}
                                    />
                                </Row>

                                <Row className="form-btn-elements">
                                    {!loadingVod && (
                                        <>
                                            <Col xs="auto">
                                                <Button className="flowly-btn orange" type="submit">
                                                    {t("Pages.Events.Submit")}
                                                </Button>
                                            </Col>
                                            <Col xs="auto" className="pt-2">
                                                {vodResult === 201 && vodValidated && (
                                                    <Form.Label style={{ color: 'green' }}>
                                                        {t("Pages.Events.Success")}
                                                    </Form.Label>
                                                )}
                                                {vodResult >= 400 && !vodValidated && (
                                                    <Form.Label style={{ color: 'red' }}>
                                                        {t("Pages.Events.Fail")}
                                                    </Form.Label>
                                                )}
                                            </Col>
                                        </>
                                    )}
                                </Row>
                                {loadingVod && <Spinner animation="border" />}
                            </Form>
                        </div>
                    )}
                </Row>
            </Section>
        </>
    );

};

export default Event;